import { FormEvent, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Check, Bell, Plus, Clock, Mail, Star, ChevronRight, X } from 'lucide-react'
import { useAuthStore } from '../store/auth'
import { useUpdateMe, useLogout } from '../hooks/useAuth'
import { useNotifications, useMarkAllRead, useUpdateNotificationPreference } from '../hooks/useNotifications'
import { useMyDashboard } from '../hooks/useMyDashboard'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Avatar } from '../components/ui/Avatar'
import { useIsMobile } from '../hooks/useIsMobile'
import type { AppNotification, Church } from '@fellowship/shared'

const DENOMINATIONS = [
  { value: '', label: 'Any / no preference' },
  { value: 'seventh-day-adventist', label: 'Seventh-day Adventist' },
  { value: 'baptist', label: 'Baptist' },
  { value: 'methodist', label: 'Methodist' },
  { value: 'non-denominational', label: 'Non-denominational' },
  { value: 'catholic', label: 'Catholic' },
  { value: 'lutheran', label: 'Lutheran' },
  { value: 'presbyterian', label: 'Presbyterian' },
]

const DENOM_SHORT: Record<string, string> = {
  'seventh-day-adventist': 'Adventist',
  baptist: 'Baptist',
  methodist: 'Methodist',
  'non-denominational': 'Non-denom.',
  catholic: 'Catholic',
  lutheran: 'Lutheran',
  presbyterian: 'Presbyterian',
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(diff / 3_600_000)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(diff / 86_400_000)
  if (days === 1) return 'Yest.'
  return `${days}d`
}

const NOTIF_ICON_CFG: Record<string, { bg: string; color: string; Icon: React.ElementType }> = {
  rsvp_approved: { bg: '#dcfce7', color: '#16a34a', Icon: Check },
  rsvp_declined: { bg: '#fee2e2', color: '#dc2626', Icon: X },
  new_event:     { bg: 'var(--primary-soft)', color: 'var(--primary)', Icon: Plus },
  reminder:      { bg: '#fef3c7', color: '#d97706', Icon: Clock },
  message:       { bg: '#f1f5f9', color: '#64748b', Icon: Mail },
  follow:        { bg: '#fce7f3', color: '#db2777', Icon: Star },
}

function NotifIcon({ type }: { type: string }) {
  const cfg = NOTIF_ICON_CFG[type] ?? { bg: 'var(--primary-soft)', color: 'var(--primary)', Icon: Bell }
  const { Icon, bg, color } = cfg
  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%', background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon size={15} strokeWidth={2.2} style={{ color }} />
    </div>
  )
}

function NotificationRow({ n }: { n: AppNotification }) {
  const row = (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
      borderRadius: 12, background: n.readAt ? 'var(--surface)' : 'var(--primary-soft)',
      border: '1px solid var(--line-soft)', cursor: n.url ? 'pointer' : 'default',
    }}>
      <div style={{
        width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 8,
        background: n.readAt ? 'transparent' : 'var(--primary)',
      }} />
      <NotifIcon type={n.type} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.45 }}>{n.title}</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2, lineHeight: 1.4 }}>{n.body}</div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', marginTop: 2, flexShrink: 0 }}>
        {timeAgo(n.createdAt)}
      </div>
    </div>
  )

  if (n.url) {
    return (
      <Link to={n.url} style={{ textDecoration: 'none', display: 'block' }} aria-label={n.title}>
        {row}
      </Link>
    )
  }
  return row
}

type FilterKey = 'all' | 'unread' | 'rsvps' | 'new_events' | 'reminders'

const FILTER_MATCH: Record<FilterKey, (n: AppNotification) => boolean> = {
  all:       () => true,
  unread:    (n) => !n.readAt,
  rsvps:     (n) => n.type.startsWith('rsvp'),
  new_events:(n) => n.type === 'new_event',
  reminders: (n) => n.type === 'reminder',
}

function ChurchFollowCard({ church }: { church: Church }) {
  return (
    <Link
      to={`/churches/${church.slug}`}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--line-soft)', textDecoration: 'none', marginBottom: 8 }}
    >
      <Avatar name={church.name} size={36} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>{church.name}</div>
        {church.denomination && (
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>
            {DENOM_SHORT[church.denomination] ?? church.denomination}
          </div>
        )}
      </div>
    </Link>
  )
}

const SECTION_LABEL_STYLE: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase',
  color: 'var(--muted)', marginBottom: 10,
}

const PREF_ROW_STYLE: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '11px 0', borderBottom: '1px solid var(--line-soft)',
}

export default function ProfilePage() {
  const { user } = useAuthStore()
  const updateMe = useUpdateMe()
  const logout = useLogout()
  const isMobile = useIsMobile()
  const [isEditing, setIsEditing] = useState(false)
  const [saved, setSaved] = useState(false)

  const [displayName, setDisplayName] = useState(user?.displayName ?? '')
  const [locationLabel, setLocationLabel] = useState(user?.locationLabel ?? '')
  const [denomination, setDenomination] = useState(user?.denomination ?? '')

  const updateNotifPref = useUpdateNotificationPreference()
  const [notifyNewEvents, setNotifyNewEvents] = useState(user?.notifyNewEvents ?? true)
  const [privacyShowLastName, setPrivacyShowLastName] = useState(user?.privacyShowLastName ?? true)
  const [privacyRsvpPublic, setPrivacyRsvpPublic] = useState(user?.privacyRsvpPublic ?? true)

  const { data: notifData, isLoading: notifLoading } = useNotifications()
  const markAll = useMarkAllRead()
  const { data: dashData } = useMyDashboard()

  const [filter, setFilter] = useState<FilterKey>('all')

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName ?? '')
      setLocationLabel(user.locationLabel ?? '')
      setDenomination(user.denomination ?? '')
      setNotifyNewEvents(user.notifyNewEvents ?? true)
      setPrivacyShowLastName(user.privacyShowLastName ?? true)
      setPrivacyRsvpPublic(user.privacyRsvpPublic ?? true)
    }
  }, [user])

  const allNotifications = notifData?.data?.notifications ?? []
  const unreadCount = notifData?.data?.unreadCount ?? 0
  const filtered = allNotifications.filter(FILTER_MATCH[filter])

  const followedChurches = dashData?.data?.followedChurches ?? []
  const rsvpCount = (dashData?.data?.upcoming?.length ?? 0) + (dashData?.data?.past?.length ?? 0)

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSaved(false)
    updateMe.mutate(
      { displayName, locationLabel, denomination: denomination || null },
      { onSuccess: () => { setSaved(true); setTimeout(() => setIsEditing(false), 800) } },
    )
  }

  const FILTERS: { key: FilterKey; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: unreadCount > 0 ? `Unread · ${unreadCount}` : 'Unread' },
    { key: 'rsvps', label: 'RSVPs' },
    { key: 'new_events', label: 'New events' },
    { key: 'reminders', label: 'Reminders' },
  ]

  return (
    <PublicLayout>
      <div style={{ maxWidth: 1060, margin: '0 auto', padding: isMobile ? '24px 16px 60px' : '40px 40px 80px', display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', gap: isMobile ? 28 : 40, alignItems: 'flex-start' }}>

        {/* ── Left: Notifications ── */}
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h1 style={{ margin: 0, fontSize: 36, fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink)' }}>
              Notifications
            </h1>
            {unreadCount > 0 && (
              <button
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Filter pills */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
            {FILTERS.map(({ key, label }) => {
              const active = filter === key
              return (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  style={{
                    height: 32, padding: '0 14px', borderRadius: 999,
                    border: active ? 'none' : '1.5px solid var(--line)',
                    background: active ? 'var(--ink)' : 'transparent',
                    color: active ? '#fff' : 'var(--ink-2)',
                    fontSize: 13, fontWeight: active ? 600 : 500,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>

          {/* Notification list */}
          {notifLoading && <p style={{ fontSize: 13, color: 'var(--muted)' }}>Loading…</p>}
          {!notifLoading && filtered.length === 0 && (
            <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
              {filter === 'all' ? 'No notifications yet.' : 'No notifications in this category.'}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.map((n) => <NotificationRow key={n.id} n={n} />)}
          </div>
        </div>

        {/* ── Right: Profile sidebar ── */}
        <div style={{ width: isMobile ? '100%' : 340, flexShrink: 0 }}>
          <div style={{ ...SECTION_LABEL_STYLE, marginBottom: 12 }}>Your profile</div>

          {!isEditing ? (
            <>
              {/* Profile card */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 20, padding: '20px 18px', marginBottom: 28, boxShadow: '0 1px 3px rgba(31,26,46,.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <Avatar name={user?.displayName ?? '?'} size={48} />
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>{user?.displayName ?? '—'}</div>
                    {user?.locationLabel && (
                      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{user.locationLabel}</div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 0, borderTop: '1px solid var(--line-soft)', borderBottom: '1px solid var(--line-soft)', padding: '12px 0', marginBottom: 14 }}>
                  {[
                    { label: 'RSVPs', value: rsvpCount },
                    { label: 'Following', value: followedChurches.length },
                  ].map(({ label, value }, i, arr) => (
                    <div key={label} style={{ flex: 1, textAlign: 'center', borderRight: i < arr.length - 1 ? '1px solid var(--line-soft)' : 'none' }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink)' }}>{value}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500, marginTop: 2 }}>{label}</div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setIsEditing(true)}
                  style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  Edit profile
                  <ChevronRight size={14} strokeWidth={2.5} />
                </button>
              </div>

              {/* Following */}
              {followedChurches.length > 0 && (
                <div style={{ marginBottom: 28 }}>
                  <div style={SECTION_LABEL_STYLE}>Following</div>
                  {followedChurches.map((c) => <ChurchFollowCard key={c.id} church={c} />)}
                </div>
              )}

              {/* Preferences */}
              <div>
                <div style={SECTION_LABEL_STYLE}>Preferences</div>
                <div style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 16, padding: '0 14px', boxShadow: '0 1px 3px rgba(31,26,46,.04)' }}>
                  <div style={{ ...PREF_ROW_STYLE }}>
                    <span style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>Notifications</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={notifyNewEvents}
                          onChange={(e) => {
                            const val = e.target.checked
                            setNotifyNewEvents(val)
                            updateNotifPref.mutate(val)
                          }}
                          aria-label="Notify me about new events from churches I follow"
                          style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--primary)' }}
                        />
                        <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>
                          {notifyNewEvents ? 'Email' : 'Off'}
                        </span>
                      </label>
                    </div>
                  </div>
                  <div style={{ ...PREF_ROW_STYLE, borderBottom: 'none' }}>
                    <span style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>Denomination</span>
                    <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>
                      {user?.denomination ? (DENOM_SHORT[user.denomination] ?? user.denomination) : 'Any'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Privacy */}
              <div style={{ marginTop: 28 }}>
                <div style={SECTION_LABEL_STYLE}>Privacy</div>
                <div style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 16, padding: '0 14px', boxShadow: '0 1px 3px rgba(31,26,46,.04)' }}>
                  <div style={{ ...PREF_ROW_STYLE }}>
                    <div>
                      <div style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>Show full name</div>
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
                        {privacyShowLastName ? 'Full name visible to hosts' : 'Only first name + initial shown'}
                      </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={privacyShowLastName}
                        onChange={(e) => {
                          const val = e.target.checked
                          setPrivacyShowLastName(val)
                          updateMe.mutate({ privacyShowLastName: val })
                        }}
                        aria-label="Show full last name to event hosts"
                        style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--primary)' }}
                      />
                      <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>
                        {privacyShowLastName ? 'On' : 'Off'}
                      </span>
                    </label>
                  </div>
                  <div style={{ ...PREF_ROW_STYLE, borderBottom: 'none' }}>
                    <div>
                      <div style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>Public RSVP history</div>
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
                        {privacyRsvpPublic ? 'Others can see events you attend' : 'Your RSVPs are private'}
                      </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={privacyRsvpPublic}
                        onChange={(e) => {
                          const val = e.target.checked
                          setPrivacyRsvpPublic(val)
                          updateMe.mutate({ privacyRsvpPublic: val })
                        }}
                        aria-label="Make my RSVP history public"
                        style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--primary)' }}
                      />
                      <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>
                        {privacyRsvpPublic ? 'On' : 'Off'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Edit form */
            <form onSubmit={handleSubmit}>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 20, padding: '20px 18px', marginBottom: 14, boxShadow: '0 1px 3px rgba(31,26,46,.04)' }}>
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="displayName" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 5 }}>
                    Display name
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="input"
                    autoComplete="name"
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="locationLabel" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 5 }}>
                    Your city or area
                  </label>
                  <input
                    id="locationLabel"
                    type="text"
                    value={locationLabel}
                    onChange={(e) => setLocationLabel(e.target.value)}
                    placeholder="e.g. Riverside, CA"
                    className="input"
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="denomination" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 5 }}>
                    Denomination
                  </label>
                  <select
                    id="denomination"
                    value={denomination}
                    onChange={(e) => setDenomination(e.target.value)}
                    className="input"
                    style={{ cursor: 'pointer' }}
                  >
                    {DENOMINATIONS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 5 }}>Email</div>
                  <div style={{ height: 40, padding: '0 12px', borderRadius: 12, background: 'var(--bg)', border: '1px solid var(--line-soft)', fontSize: 13.5, color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
                    {user?.email}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>
                    <Link to="/forgot-password" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 500 }}>
                      Change password
                    </Link>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button type="submit" disabled={updateMe.isPending} className="btn" style={{ flex: 1 }}>
                  {updateMe.isPending ? 'Saving…' : saved ? 'Saved ✓' : 'Save changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  style={{ height: 44, padding: '0 16px', borderRadius: 12, border: '1.5px solid var(--line)', background: 'transparent', fontSize: 13.5, fontWeight: 500, color: 'var(--ink-2)', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  Cancel
                </button>
              </div>

              <div style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 16, padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink)' }}>Sign out</div>
                <button
                  type="button"
                  onClick={() => logout.mutate()}
                  style={{ height: 34, padding: '0 14px', borderRadius: 999, background: 'transparent', border: '1.5px solid var(--line)', fontSize: 12.5, fontWeight: 500, color: 'var(--ink-2)', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  Sign out
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </PublicLayout>
  )
}
